// Tenant feature switches.
//
// Backed by apps/admin/src/routes/featureFlags.ts:
//   GET /feature-flags           (every known switch + its effective value)
//   PUT /feature-flags/:key      (flip one, body { enabled })
//
// Unlike the Precedent Analytics hooks next door, there is no 501 /
// "Pending backend" state to handle: these routes need only Firestore, which
// every environment has. A switch whose availability varied by environment
// would be the exact problem this replaced.
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { useApi } from "./api";

export type FeatureFlag = {
  key: string;
  label: string;
  description: string;
  enabled: boolean;
  /** False → the tenant has never been given an explicit value and is running
   *  on the platform default. Shown so an admin can tell "nobody chose this"
   *  from "someone chose this". */
  explicitlySet: boolean;
};

type FeatureFlagsResponse = { flags: FeatureFlag[] };

export function useFeatureFlags(
  tenantId: string
): UseQueryResult<FeatureFlagsResponse, Error> {
  const api = useApi("admin");
  return useQuery({
    queryKey: ["feature-flags", tenantId],
    queryFn: () => api<FeatureFlagsResponse>("/feature-flags"),
  });
}

export function useSetFeatureFlag(
  tenantId: string
): UseMutationResult<
  FeatureFlagsResponse,
  Error,
  { key: string; enabled: boolean }
> {
  const api = useApi("admin");
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ key, enabled }) =>
      api<FeatureFlagsResponse>(`/feature-flags/${encodeURIComponent(key)}`, {
        method: "PUT",
        body: { enabled },
      }),
    // The PUT returns the full resolved list, so seed the cache with it rather
    // than invalidating and refetching — the toggle settles in one round trip.
    onSuccess: (data) => {
      queryClient.setQueryData(["feature-flags", tenantId], data);
    },
  });
}
